import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { memo } from "react";
import ComparisonTable from "../components/ComparisonTable";
import { useTheme } from "@mui/material/styles";
import {
    ComparisonValidityState,
    IComparisonInfo,
} from "../declarations/types";
import type { ExplorerEntityType, DetailPanelContent, IPropertySection } from "../declarations/IExplorerTypes";
import type { ILineExistenceInfo } from "../hooks/SalesOrderLine/useGetSalesOrderLineExistence";
import { getNodeColor } from "./nodes/nodeColors";

interface IExplorerDetailPanelProps {
    selectedEntityType: ExplorerEntityType | null;
    content: DetailPanelContent;
    onClose: () => void;
}

function getEntityLabel(entityType: ExplorerEntityType): string {
    switch (entityType) {
        case "SalesOrderHeader":
            return "Sales Order Header Detail";
        case "Customer":
            return "Customer Detail";
        case "VatNum":
            return "VAT Number Detail";
        case "SalesOrderHeaderCharge":
            return "Header Charges Detail";
        case "SalesOrderLines":
            return "Sales Order Lines Detail";
        case "SalesOrderLineDetail":
            return "Sales Order Line Detail";
        case "Product":
            return "Product Detail";
        case "ReleasedProduct":
            return "Released Product Detail";
        case "Route":
            return "Route Detail";
        case "RouteOperationDetail":
            return "Operation Detail";
        case "BomLines":
            return "BOM Lines Detail";
        case "FMRoute":
            return "FileMaker Manufacturing Route";
    }
}

function computeChipSummary(data: IComparisonInfo[] | undefined) {
    const totalFields = data?.length ?? 0;
    const validCount = data?.filter((d) => d.validity === ComparisonValidityState.Valid).length ?? 0;
    const invalidCount = data?.filter((d) => d.validity === ComparisonValidityState.Invalid).length ?? 0;
    const warningCount = data?.filter((d) => d.validity === ComparisonValidityState.Warning).length ?? 0;
    return { totalFields, validCount, invalidCount, warningCount };
}

function ComparisonChips({ data }: { data: IComparisonInfo[] | undefined }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const { totalFields, validCount, invalidCount, warningCount } = computeChipSummary(data);
    if (totalFields === 0) {
        return null;
    }
    return (
        <Stack direction="row" spacing={1} mt={1}>
            <Chip
                label={`${validCount} matched`}
                size="small"
                sx={{
                    backgroundColor: isDark ? "rgba(76, 175, 80, 0.25)" : "#c8e6c9",
                    color: isDark ? "#81c784" : "#2e7d32",
                    fontSize: "0.75rem",
                    height: 24,
                }}
            />
            {invalidCount > 0 && (
                <Chip
                    label={`${invalidCount} mismatch`}
                    size="small"
                    sx={{
                        backgroundColor: isDark ? "rgba(244, 67, 54, 0.25)" : "#ffcdd2",
                        color: isDark ? "#ef9a9a" : "#c62828",
                        fontSize: "0.75rem",
                        height: 24,
                    }}
                />
            )}
            {warningCount > 0 && (
                <Chip
                    label={`${warningCount} warning`}
                    size="small"
                    sx={{
                        backgroundColor: isDark ? "rgba(255, 152, 0, 0.25)" : "#fff3e0",
                        color: isDark ? "#ffcc80" : "#e65100",
                        fontSize: "0.75rem",
                        height: 24,
                    }}
                />
            )}
            <Typography
                variant="caption"
                sx={{ color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)", alignSelf: "center" }}
            >
                {totalFields} fields total
            </Typography>
        </Stack>
    );
}

function RouteComparisonChips({ headerData, versionData }: { headerData?: IComparisonInfo[]; versionData?: IComparisonInfo[] }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const h = computeChipSummary(headerData);
    const v = computeChipSummary(versionData);
    const totalFields = h.totalFields + v.totalFields;
    const validCount = h.validCount + v.validCount;
    const invalidCount = h.invalidCount + v.invalidCount;
    const warningCount = h.warningCount + v.warningCount;
    if (totalFields === 0) {
        return null;
    }
    return (
        <Stack direction="row" spacing={1} mt={1}>
            <Chip
                label={`${validCount} matched`}
                size="small"
                sx={{
                    backgroundColor: isDark ? "rgba(76, 175, 80, 0.2)" : "#c8e6c9",
                    color: isDark ? "#81c784" : "#2e7d32",
                    fontSize: "0.75rem",
                    height: 24,
                }}
            />
            {invalidCount > 0 && (
                <Chip
                    label={`${invalidCount} mismatch`}
                    size="small"
                    sx={{
                        backgroundColor: isDark ? "rgba(244, 67, 54, 0.2)" : "#ffcdd2",
                        color: isDark ? "#ef9a9a" : "#c62828",
                        fontSize: "0.75rem",
                        height: 24,
                    }}
                />
            )}
            {warningCount > 0 && (
                <Chip
                    label={`${warningCount} warning`}
                    size="small"
                    sx={{
                        backgroundColor: isDark ? "rgba(255, 152, 0, 0.2)" : "#fff3e0",
                        color: isDark ? "#ffcc80" : "#e65100",
                        fontSize: "0.75rem",
                        height: 24,
                    }}
                />
            )}
            <Typography
                variant="caption"
                sx={{ color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)", alignSelf: "center" }}
            >
                {totalFields} fields total
            </Typography>
        </Stack>
    );
}

function LineExistenceTable({ data }: { data: ILineExistenceInfo[] }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const bothCount = data.filter((d) => d.existsInCS && d.existsInD365).length;
    const csOnlyCount = data.filter((d) => d.existsInCS && !d.existsInD365).length;
    const d365OnlyCount = data.filter((d) => !d.existsInCS && d.existsInD365).length;

    return (
        <Box>
            <Stack direction="row" spacing={1} px={1} py={1}>
                <Chip
                    label={`${bothCount} matched`}
                    size="small"
                    sx={{
                        backgroundColor: isDark ? "rgba(76, 175, 80, 0.2)" : "#c8e6c9",
                        color: isDark ? "#81c784" : "#2e7d32",
                        fontSize: "0.75rem",
                        height: 24,
                    }}
                />
                {csOnlyCount > 0 && (
                    <Chip
                        label={`${csOnlyCount} CS only`}
                        size="small"
                        sx={{
                            backgroundColor: isDark ? "rgba(244, 67, 54, 0.2)" : "#ffcdd2",
                            color: isDark ? "#ef9a9a" : "#c62828",
                            fontSize: "0.75rem",
                            height: 24,
                        }}
                    />
                )}
                {d365OnlyCount > 0 && (
                    <Chip
                        label={`${d365OnlyCount} D365 only`}
                        size="small"
                        sx={{
                            backgroundColor: isDark ? "rgba(255, 152, 0, 0.2)" : "#fff3e0",
                            color: isDark ? "#ffcc80" : "#e65100",
                            fontSize: "0.75rem",
                            height: 24,
                        }}
                    />
                )}
            </Stack>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Inventory Lot ID</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Item</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }} align="center">CS</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }} align="center">D365</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((line) => {
                            const isMissing = !line.existsInCS || !line.existsInD365;
                            return (
                                <TableRow
                                    key={line.inventoryLotId}
                                    sx={{
                                        backgroundColor: isMissing
                                            ? (isDark ? "rgba(244, 67, 54, 0.08)" : "rgba(244, 67, 54, 0.04)")
                                            : undefined,
                                    }}
                                >
                                    <TableCell sx={{ fontSize: "0.75rem" }}>{line.inventoryLotId}</TableCell>
                                    <TableCell sx={{ fontSize: "0.75rem" }}>{line.itemNumber}</TableCell>
                                    <TableCell align="center">
                                        {line.existsInCS ? (
                                            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: isDark ? "#81c784" : "#4caf50" }} />
                                        ) : (
                                            <ErrorOutlineIcon sx={{ fontSize: 16, color: isDark ? "#ef9a9a" : "#f44336" }} />
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {line.existsInD365 ? (
                                            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: isDark ? "#81c784" : "#4caf50" }} />
                                        ) : (
                                            <ErrorOutlineIcon sx={{ fontSize: 16, color: isDark ? "#ef9a9a" : "#f44336" }} />
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

function PropertySections({ sections }: { sections: IPropertySection[] }) {
    const theme = useTheme();
    return (
        <Box>
            {sections.map((section, sIdx) => (
                <Box key={sIdx} sx={{ mb: 2 }}>
                    <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ px: 1, py: 0.5, backgroundColor: theme.palette.action.hover, borderRadius: 1 }}
                    >
                        {section.title}
                    </Typography>
                    <Table size="small">
                        <TableBody>
                            {section.fields.map((field, fIdx) => (
                                <TableRow key={fIdx}>
                                    <TableCell sx={{ fontWeight: 500, fontSize: "0.8rem", width: "40%", whiteSpace: "nowrap" }}>
                                        {field.label}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem", wordBreak: "break-all" }}>
                                        {field.value === null || field.value === undefined || field.value === ""
                                            ? "—"
                                            : typeof field.value === "boolean"
                                                ? (field.value ? "Yes" : "No")
                                                : String(field.value)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            ))}
        </Box>
    );
}

function RouteComparisonContent({ headerData, versionData }: { headerData?: IComparisonInfo[]; versionData?: IComparisonInfo[] }) {
    return (
        <Box>
            <Accordion defaultExpanded disableGutters elevation={0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" fontWeight={600}>Route Header</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    {headerData && headerData.length > 0 ? (
                        <ComparisonTable data={headerData} />
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No route header comparison data available.
                        </Typography>
                    )}
                </AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded disableGutters elevation={0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" fontWeight={600}>Route Version</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    {versionData && versionData.length > 0 ? (
                        <ComparisonTable data={versionData} />
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No route version comparison data available.
                        </Typography>
                    )}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}

function renderHeaderChips(content: DetailPanelContent) {
    switch (content.type) {
        case "comparison":
            return <ComparisonChips data={content.data} />;
        case "routeComparison":
            return <RouteComparisonChips headerData={content.headerData} versionData={content.versionData} />;
        default:
            return null;
    }
}

function renderBody(content: DetailPanelContent) {
    switch (content.type) {
        case "loading":
            return (
                <Box sx={{ p: 3, display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                        Loading comparison data...
                    </Typography>
                </Box>
            );
        case "error":
            return (
                <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="body2" color="error">
                        {content.message}
                    </Typography>
                </Box>
            );
        case "comparison":
            return <ComparisonTable data={content.data} />;
        case "routeComparison":
            return <RouteComparisonContent headerData={content.headerData} versionData={content.versionData} />;
        case "lineExistence":
            return <LineExistenceTable data={content.data} />;
        case "properties":
            return <PropertySections sections={content.sections} />;
        case "empty":
            return (
                <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                        {content.message ?? "No comparison data available for this entity."}
                    </Typography>
                </Box>
            );
    }
}

function ExplorerDetailPanel({
    selectedEntityType,
    content,
    onClose,
}: IExplorerDetailPanelProps) {
    const theme = useTheme();
    const nodeColor = selectedEntityType ? getNodeColor(selectedEntityType) : theme.palette.primary.main;

    if (!selectedEntityType) {
        return (
            <Box
                sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                }}
            >
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    Select a node to view CS vs D365 comparison details
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            key={selectedEntityType}
            className="detail-panel-content"
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    p: 2,
                    backgroundColor: nodeColor,
                    color: "#fff",
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6" fontWeight={600}>
                        {getEntityLabel(selectedEntityType)}
                    </Typography>
                    <IconButton size="small" onClick={onClose} sx={{ color: "inherit" }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>

                {renderHeaderChips(content)}
            </Box>

            <Divider />

            <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
                {renderBody(content)}
            </Box>
        </Box>
    );
}

export default memo(ExplorerDetailPanel);
